/**
 * منوی شناور، بیرون از هر ظرفِ محدودکننده
 * ==========================================
 * چرا این کامپوننت لازم شد — و چرا `z-index` بیشتر جوابش نبود:
 *
 * کلاس `.card` سایت `backdrop-blur` دارد. طبق استاندارد CSS، هر
 * `backdrop-filter` یک **زمینه چیدمان (stacking context)** تازه می‌سازد.
 * یعنی هر چیزی داخل یک کارت، هرقدر هم `z-index` بزرگ داشته باشد، **داخل
 * همان کارت زندانی است** و نمی‌تواند از کارتِ بعدی بالاتر بیاید.
 *
 * نتیجه‌اش در صفحه معامله این بود: منوی انتخاب ارز باز می‌شد ولی **زیر
 * نمودار** می‌رفت — چون کارت نمودار در DOM بعد از کارت هدر است و هر دو
 * `z-index: auto`اند، پس دیرتری روی زودتری کشیده می‌شود.
 *
 * راه‌حل: منو اصلاً داخل آن کارت رندر نشود. با پورتال می‌رود زیر `body` و
 * با موقعیت `fixed` به دکمه‌اش چسبانده می‌شود. آن‌وقت نه زمینه چیدمانی
 * محدودش می‌کند، نه `overflow` کارتی می‌بُردش.
 */
import { ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useMediaQuery } from '../lib/media'

type Props = {
  open: boolean
  onClose: () => void
  /** دکمه‌ای که منو باید کنارش بنشیند */
  anchor: React.RefObject<HTMLElement>
  children: ReactNode
  /** عرض منو به پیکسل */
  width?: number
  className?: string
  /**
   * روی صفحه کوچک، به‌جای منوی چسبیده به دکمه، از پایین صفحه بالا بیاید.
   *
   * چرا: منوی شناور روی گوشی یا نصفه بیرون از صفحه می‌افتد یا آن‌قدر
   * کوچک می‌شود که دکمه‌هایش با انگشت گرفتنی نیستند. برگه پایینی همان
   * الگویی است که کاربر گوشی از هر اپ دیگری می‌شناسد و شست هم به آن
   * می‌رسد.
   */
  sheet?: boolean
}

export function Popover({
  open, onClose, anchor, children, width = 352, className, sheet = true,
}: Props) {
  // شرطِ برگه‌شدن: صفحه باریک، یا لمسیِ نه‌چندان پهن. تبلت افقی و لپ‌تاپ
  // لمسی همان منوی شناور را می‌گیرند؛ روی آن‌ها برگه‌ی هشتاد‌درصدیِ یک صفحه
  // کوتاه، کشوی تنگی می‌شد.
  const small = useMediaQuery('(max-width: 640px), (pointer: coarse) and (max-width: 900px)')
  const asSheet = sheet && small
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null)
  const panel = useRef<HTMLDivElement>(null)

  // `useLayoutEffect` و نه `useEffect`: موقعیت باید **پیش از** نخستین رنگ‌آمیزی
  // حساب شود، وگرنه منو یک فریم گوشه صفحه ظاهر می‌شود و بعد می‌پرد سر جایش.
  useLayoutEffect(() => {
    if (!open) return
    const place = () => {
      const el = anchor.current
      if (!el) return
      const r = el.getBoundingClientRect()
      const gap = 8
      const vw = window.innerWidth
      const vh = window.innerHeight
      const w = Math.min(width, vw - 16)

      // سایت راست‌به‌چپ است: لبه راستِ منو با لبه راستِ دکمه هم‌تراز می‌شود.
      let left = r.right - w
      left = Math.max(8, Math.min(left, vw - w - 8))

      // اگر پایین جا نبود، بالای دکمه باز شود
      let top = r.bottom + gap
      const h = panel.current?.offsetHeight ?? 380
      if (top + h > vh - 8 && r.top - gap - h > 8) top = r.top - gap - h
      top = Math.max(8, Math.min(top, Math.max(8, vh - h - 8)))

      setPos({ top, left })
    }
    place()
    // با اسکرول و تغییر اندازه، منو باید همراه دکمه بماند.
    // `capture` لازم است تا اسکرولِ ظرف‌های داخلی هم شنیده شود.
    window.addEventListener('scroll', place, true)
    window.addEventListener('resize', place)
    return () => {
      window.removeEventListener('scroll', place, true)
      window.removeEventListener('resize', place)
    }
  }, [open, anchor, width])

  // قفل اسکرول صفحه پشتِ برگه: بدون این، انگشتی که روی برگه می‌کشد صفحه
  // زیرش را حرکت می‌دهد و روی iOS کل صفحه کش می‌آید.
  useEffect(() => {
    if (!open || !asSheet || typeof document === 'undefined') return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prev }
  }, [open, asSheet])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open || typeof document === 'undefined') return null

  if (asSheet) {
    return createPortal(
      <>
        <div className="fixed inset-0 z-[900] bg-black/50 backdrop-blur-[2px]"
          onClick={onClose} />
        <div
          className={`fixed inset-x-0 bottom-0 z-[901] max-h-[80vh] overflow-y-auto
            rounded-t-3xl border-t border-white/10 bg-ink-900/98 p-4 pb-[max(1rem,env(safe-area-inset-bottom))]
            shadow-card backdrop-blur-2xl ${className ?? ''}`}
        >
          {/* دستگیره: به کاربر می‌گوید این برگه بسته‌شدنی است */}
          <button onClick={onClose} aria-label="بستن"
            className="mx-auto mb-3 block h-1.5 w-12 rounded-full bg-white/20" />
          {children}
        </div>
      </>,
      document.body,
    )
  }

  return createPortal(
    <>
      {/* پرده نامرئی برای بستن با کلیک بیرون */}
      <div className="fixed inset-0 z-[900]" onClick={onClose} />
      <div
        ref={panel}
        style={{ top: pos?.top ?? -9999, left: pos?.left ?? -9999, width }}
        className={`fixed z-[901] rounded-2xl border border-white/10 bg-ink-900/97 p-3 shadow-card backdrop-blur-2xl ${className ?? ''}`}
      >
        {children}
      </div>
    </>,
    document.body,
  )
}
